/**
 * The document detail page's read-only Folder value (L18-10).
 *
 * The folder name comes from the one folder list (lib/use-library-folders.ts).
 * Each state says what is true:
 *   - no folder              → "No folder (root)"
 *   - the folder is listed   → its name — also when a background refetch
 *     failed but the cached list still names it (TanStack Query keeps
 *     `data` on a failed refetch; LB2T3-BF-05)
 *   - the list read failed   → an error, never a guessed name
 *   - the list is loading    → "Loading folder…"
 *   - the list was read and does not hold the document's folder → "Folder not
 *     found" (the folder is gone; the foreign key sends the document to the
 *     root, so a reload shows the root).
 * There is no "unknown folder" fallback for a folder the API returned.
 */
export type FolderListStatus = 'pending' | 'error' | 'success';

export interface DocumentFolderValueProps {
  folderId: string | null;
  folders: ReadonlyArray<{ id: string; name: string }>;
  status: FolderListStatus;
}

export function DocumentFolderValue({ folderId, folders, status }: DocumentFolderValueProps) {
  if (!folderId) {
    return <p className="text-sm text-muted-foreground italic">No folder (root)</p>;
  }
  // A name the list (even a cached one) holds is a fact; show it first.
  const folder = folders.find((f) => f.id === folderId);
  if (folder) {
    return (
      <p className="text-sm" data-testid="document-folder-name">
        {folder.name}
      </p>
    );
  }
  if (status === 'error') {
    return (
      <p className="text-sm text-destructive" role="alert" data-testid="document-folder-error">
        The folder list could not be loaded.
      </p>
    );
  }
  if (status === 'pending') {
    return <p className="text-sm text-muted-foreground italic">Loading folder…</p>;
  }
  return (
    <p className="text-sm text-muted-foreground italic" data-testid="document-folder-missing">
      Folder not found
    </p>
  );
}
